import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAudit } from "../context/AuditContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { User, Briefcase, FolderOpen, ArrowRight, ShieldCheck } from "lucide-react";
import superAdminAvatar from "../assets/OIP.jpeg";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  audit_manager: "Audit Manager",
  senior_auditor: "Senior Auditor",
  auditor: "Auditor",
  external_auditor: "External Auditor",
  control_owner: "Control Owner",
  observer: "Observer",
};

function isAssignedToRun(run, userId) {
  return (run?.assigned_users || []).some((item) => {
    if (typeof item === "string") return item === userId;
    return item?.user_id === userId;
  });
}

function formatAuditTimestamp(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { auditRuns, fetchRuns, loadRun } = useAudit();
  const departmentValue =
    user?.department || (user?.role === "super_admin" ? "Enterprise Governance" : "-");
  const processAreasValue =
    Array.isArray(user?.process_areas) && user.process_areas.length > 0
      ? user.process_areas.join(", ")
      : user?.role === "super_admin"
        ? "Platform Oversight, Audit Operations, Risk Monitoring"
        : "-";

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const summary = useMemo(() => {
    const runs = auditRuns || [];
    const owned = runs.filter((run) => run.created_by === user?.id).length;
    const assigned = runs.filter((run) => isAssignedToRun(run, user?.id)).length;
    return {
      total: runs.length,
      owned,
      assigned,
    };
  }, [auditRuns, user?.id]);

  const visibleAuditsLabel = useMemo(() => {
    if (!user?.role) return "Accessible audits";
    const role = ROLE_LABELS[user.role] || user.role;
    return `${role} audits`;
  }, [user?.role]);

  const openAudit = async (run) => {
    try {
      const loaded = await loadRun(run.id);
      navigate(`/phase/${loaded?.current_phase || 1}`);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
        logout();
        navigate("/login", { replace: true });
        return;
      }
      if (error?.response?.status === 403) {
        toast.error("You do not have access to this audit.");
        return;
      }
      toast.error(error?.response?.data?.detail || "Failed to open audit");
    }
  };

  return (
    <div className="space-y-6 max-w-[1380px]" data-testid="profile-page">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Profile</h2>
        <p className="text-sm text-muted-foreground mt-1">Your access profile and the audits visible to your role.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[420px_minmax(0,1fr)]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4" />
              Account Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="profile-summary-avatar flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-primary/35 bg-primary/10">
                {user?.role === "super_admin" ? (
                  <img
                    src={superAdminAvatar}
                    alt="Super Admin"
                    className="h-full w-full object-cover object-top"
                  />
                ) : (
                  <span className="text-sm font-semibold">{String(user?.name || "?").trim().charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate">{user?.name || "Unknown user"}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email || "-"}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Role</span>
                <Badge variant="outline">{ROLE_LABELS[user?.role] || user?.role || "Unknown"}</Badge>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Department</span>
                <span>{departmentValue}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Process Areas</span>
                <span className="text-right">{processAreasValue}</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-2">
              <div className="rounded-lg border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Total</p>
                <p className="text-lg font-semibold">{summary.total}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Owned</p>
                <p className="text-lg font-semibold">{summary.owned}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Assigned</p>
                <p className="text-lg font-semibold">{summary.assigned}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              {visibleAuditsLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {auditRuns.length === 0 ? (
              <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                No audits are visible for this account yet.
              </div>
            ) : (
              auditRuns.map((run) => {
                const owned = run.created_by === user?.id;
                const assigned = isAssignedToRun(run, user?.id);
                return (
                  <div key={run.id} className="flex items-center justify-between gap-4 rounded-xl border p-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium truncate">{run.name}</p>
                        {owned && (
                          <Badge variant="outline" className="gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            Owner
                          </Badge>
                        )}
                        {assigned && !owned && (
                          <Badge variant="secondary" className="gap-1">
                            <Briefcase className="w-3 h-3" />
                            Assigned
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Phase {run.current_phase || 1} • Status: {run.status || "active"}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span>Created: {formatAuditTimestamp(run.created_at)}</span>
                        <span>Modified: {formatAuditTimestamp(run.updated_at)}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openAudit(run)}>
                      Open Audit
                      <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
